import Button from '@doordash/component-button'
import { Text, InlineChildren, StackChildren } from '@doordash/design-language'
import * as React from 'react'
import { connect } from 'react-redux'
import * as events from '../../../events'
import { CheckrStatus, ShippingStatus } from '../../../models/types'
import {
  detectWebviewOs,
  getMobileDevice,
  MobileDevice,
} from '../../../utilities/userAgent'
import { AppDownloadOS } from '../../atoms/app-download-button'
import ActivationKitModal from '../../molecules/activation-kit-modal'

import { OsTypes } from '../../../webview/types'

import { activation, redCard } from './strings'

const HELP_PREFIX = 'https://help.doordash.com/dashers/s/article/'
const DASHER_HELP_LINK = `${HELP_PREFIX}How-do-I-setup-and-use-the-Dasher-Red-Card`

const {
  shippedText,
  beingPreparedText,
  futureMessage,
  pastMessage,
  pastMessageWithDate,
  statusLabel,
  statusInProgress,
  statusDelivered,
  expectedDeliveryDateLabel,
  trackButton,
  activationKitModalButton,
} = activation

type RedCardActivationStepProps = {
  isActive: boolean
  isComplete: boolean
  pageLink: () => Promise<any>
  address: string
  shippingUrl: string
  shippingArrivalDate: string
  shippingStatus: ShippingStatus
  bgcheckStatus: CheckrStatus
  trackConfirmationPageTapDownloadApp?: () => Promise<any>
  trackConfirmationPageClick?: (name: string, data?: object) => Promise<any>
  experiments: any
}

const getShippedCopy = (
  bgcheckStatus,
  shippingStatus,
  active,
  address,
  date
) => {
  // Future message
  if (bgcheckStatus !== CheckrStatus.Clear) {
    // tslint:disable-next-line:max-line-length
    return futureMessage(address)
  }

  // Past Message
  if (shippingStatus === ShippingStatus.Delivered) {
    return date ? pastMessageWithDate(address, date) : pastMessage(address)
  }

  // Active and package is shipped
  if (shippingStatus === ShippingStatus.Shipped && active) {
    return shippedText(address)
  }

  // Being Prepared
  return beingPreparedText(address)
}

const getDownloadCopy = (isActive, isComplete) => {
  if (isActive || isComplete) {
    return null
  }

  // Future Copy, overrides default download App copy
  if (!isActive && !isComplete) {
    return redCard.recievedKit()
  }

  return null
}

class RedCardActivationStep extends React.Component<
  RedCardActivationStepProps
> {
  public state = {
    isShowingModal: false,
  }

  public activateRedCardButton(device, status) {
    const {
      isActive,
      isComplete,
      trackConfirmationPageTapDownloadApp,
      experiments,
    } = this.props

    const data = {
      os: AppDownloadOS.DESKTOP,
      type: Button.Types.TertiaryPill,
      isInline: true,
      size: Button.Sizes.Small,
      name: redCard.recievedKit(),
      onClick: () => trackConfirmationPageTapDownloadApp,
    }

    if (device === MobileDevice.iOS) {
      data.os = AppDownloadOS.IOS
    }
    if (device === MobileDevice.Android) {
      data.os = AppDownloadOS.ANDROID
    }

    if (status === ShippingStatus.Delivered) {
      data.type = Button.Types.PrimaryPill
      data.name = experiments.bank_onboarding
        ? redCard.activate()
        : getDownloadCopy(isActive, isComplete)
    }

    return (
      <Button
        type={Button.Types.PrimaryPill}
        isInline={true}
        size={Button.Sizes.Small}
        os={'desktop'}
        name={'Activate Red Card'}
        onClick={this.props.pageLink}
      >
        {redCard.activate()}
      </Button>
    )
  }
  public toggleModal(e) {
    e.stopPropagation()

    const isShowingModal = !this.state.isShowingModal

    this.setState({
      isShowingModal,
    })
  }
  public render() {
    const {
      isActive,
      address,
      shippingArrivalDate,
      bgcheckStatus,
      shippingStatus,
      trackConfirmationPageClick,
      experiments,
    } = this.props
    const device = getMobileDevice()
    const isWebview = detectWebviewOs() !== OsTypes.NONE

    const legacyActivateRedCardStep = (
      <StackChildren>
        <Text styles={Text.Styles.SectionSubtext}>{redCard.legacyBody()}</Text>
        <InlineChildren>
          {this.activateRedCardButton(device, ShippingStatus.Delivered)}
          {!isWebview && (
            <Button
              type={Button.Types.SecondaryPill}
              isInline={true}
              size={Button.Sizes.Small}
              href={DASHER_HELP_LINK}
              onClick={e =>
                trackConfirmationPageClick('red_card_activation_help') &&
                e.stopPropagation()
              }
              tag="a"
            >
              {redCard.helpButton()}
            </Button>
          )}
        </InlineChildren>
      </StackChildren>
    )

    const redCardEntryContentBodyFuture = (
      <Text styles={Text.Styles.SectionSubtext}>{redCard.body()}</Text>
    )

    // tslint:disable-next-line:max-line-length
    const activateRedCardContentBody = (
      <StackChildren size={StackChildren.Sizes.XxSmall}>
        <Text styles={Text.Styles.SectionSubtext}>
          {getShippedCopy(
            bgcheckStatus,
            shippingStatus,
            isActive,
            address,
            shippingArrivalDate
          )}
        </Text>
        <Button
          onClick={e => {
            trackConfirmationPageClick('activation_kit_button')
            this.toggleModal(e)
          }}
          type={Button.Types.FlatPrimary}
          size={Button.Sizes.Small}
          padding={Button.Padding.None}
          isInline
        >
          {activationKitModalButton()}
        </Button>
        {shippingStatus !== ShippingStatus.NotCreated &&
          shippingStatus !== ShippingStatus.Error && (
            <div>
              <Text>
                {`${statusLabel()}: `}
                <Text
                  display={Text.DisplayModes.Inline}
                  weight={Text.Weights.Bold}
                >
                  {shippingStatus === ShippingStatus.Delivered
                    ? statusDelivered()
                    : statusInProgress()}
                </Text>
              </Text>
              {shippingArrivalDate && (
                <Text>
                  {`${expectedDeliveryDateLabel()}: `}
                  <Text
                    display={Text.DisplayModes.Inline}
                    weight={Text.Weights.Bold}
                  >
                    {shippingArrivalDate}
                  </Text>
                </Text>
              )}
            </div>
          )}
        <InlineChildren>
          {shippingStatus === ShippingStatus.Delivered &&
            // In V2 this actually initials the red card entry from webview
            this.activateRedCardButton(device, shippingStatus)}
          {this.props.shippingUrl && (
            <Button
              type={Button.Types.TertiaryPill}
              size={Button.Sizes.Small}
              href={this.props.shippingUrl}
              tag="a"
              onClick={e => {
                this.props.trackConfirmationPageClick('track_package')
                e.stopPropagation()
              }}
              isInline
            >
              {trackButton()}
            </Button>
          )}
          {shippingStatus !== ShippingStatus.Delivered &&
            this.activateRedCardButton(device, shippingStatus)}
        </InlineChildren>
        {this.state.isShowingModal && (
          <ActivationKitModal
            onClose={e => {
              this.props.trackConfirmationPageClick(
                'activation_kit_modal_close'
              )
              this.toggleModal(e)
            }}
          />
        )}
      </StackChildren>
    )

    const activateRedCardStep =
      bgcheckStatus === CheckrStatus.Clear
        ? activateRedCardContentBody
        : redCardEntryContentBodyFuture

    return (
      <div>
        {experiments.bank_onboarding
          ? activateRedCardStep
          : legacyActivateRedCardStep}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  experiments: state.experiments,
})

const mapDispatchToProps = {
  trackConfirmationPageClick: events.trackConfirmationPageClick,
  trackConfirmationPageTapDownloadApp:
    events.trackConfirmationPageTapDownloadApp,
}
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(RedCardActivationStep)
